// Imports => React
import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  Suspense,
  lazy,
} from "react";
import { Link } from "react-router-dom";
import { withStore } from "@stores";
import { observer } from "mobx-react-lite";
import moment from "moment";
import clsx from "clsx";

// Imports => Config
import config from "@config";

// Imports => Assets
import BalanceHero2x from "@assets/images/balance-hero-2x.png";
import SupportHero2x from "@assets/images/support-hero-2x.png";
import TwoFaHero2x from "@assets/images/2fa-hero-2x.png";

// Imports => Constants
import { ROUTES } from "@constants";
import { ICONS, KEYS, THEMES, TYPES, VARIANTS } from "@opp/ui-kit";

// Imports => Utilities
import { AcIsSet, AcIsArray } from "@opp/ui-kit";

// Imports => Molecules
import AcBalanceBreakDownModal from "@molecules/ac-balance-breakdown-modal/ac-balance-breakdown-modal";
import AcAddBalanceModal from "@molecules/ac-add-balance-modal/ac-add-balance-modal";
import AcWithdrawBalanceModal from "@molecules/ac-withdraw-balance-modal/ac-withdraw-balance-modal";

// Imports => Components
const AcIncomingOutgoingChart = lazy(
  () =>
    import("@components/ac-incoming-outgoing-chart/ac-incoming-outgoing-chart")
);
const AcTransactionList = lazy(
  () => import("@components/ac-transaction-list/ac-transaction-list")
);

// Imports => Atoms
import {
  AcContainer,
  AcRow,
  AcColumn,
  AcHeading,
  AcRichContent,
  AcCard,
  AcLoaderInline,
  AcEmptyBlock,
  AcButton,
  AcIcon,
  AcImage,
} from "@opp/ui-kit";
import { AcFormatCurrencyAmount } from "../../../utilities";
import { withTranslation } from "react-i18next";
import { useGetBalanceQuery } from "@src/api/openapi/merchantApi";

const _CLASSES = {
  MAIN: "ac-dashboard-overview-tab ac-dashboard-overview__grid-container",
  UNEVEN:
    "ac-dashboard-overview-tab ac-dashboard-overview__grid-container ac-dashboard-overview__grid-container--uneven",
};

const AcDashboardOverviewTab = ({
  data,
  store: { ui, movements, global, profile, balance, settings },
  t,
  i18n,
}) => {
  const { current_movements, is_loading } = movements;
  const { current_chart_data } = global;
  const { current_profile, current_partner_currency } = profile;

  const { data: balanceData, isFetching } = useGetBalanceQuery();
  const current_balance = balanceData?.data;
  const current_balance_is_loading = balance.is_loading;

  const { has_virtual_iban, has_continuous_settlement_interval, configurations } = settings;

  const current_profile_is_loading = profile.is_loading;

  const [selectedRange, setSelectedRange] = useState("7");

  useEffect(() => {
    movements.setPageNumber(1);
    movements.list(t, {
      order: "-date",
      date_start: "01-01-2000",
      perpage: 4,
      force_simple_pagination: true,
    });

    profile.list_profiles(t);
  }, []);

  useEffect(() => {
    const today = moment();
    const date_start = today.clone().subtract(7, "days").format("DD-MM-YYYY");
    const date_end = today.clone().format("DD-MM-YYYY");
    global.get_chart_data(t, {
      date_start,
      date_end,
    });
  }, []);

  const twoFactorIsReady = useMemo(() => {
    if (!AcIsSet(current_profile)) return false;

    const { two_factor } = current_profile;

    return !two_factor;
  }, [current_profile.two_factor]);

  const getCurrentBalanceFormatted = useMemo(() => {
    if (current_balance_is_loading) {
      return <AcLoaderInline dense cover loading />;
    }
    if (!AcIsSet(current_balance)) {
      return "";
    }

    const amount = current_balance?.amount - current_balance?.amount_planned;
    return (
      <>
        {AcFormatCurrencyAmount(
          amount,
          current_balance.currency,
          i18n.resolvedLanguage,
          true
        )}
      </>
    );
  }, [current_balance, current_balance_is_loading, i18n.resolvedLanguage]);

  const displayBalanceBreakdownModal = async (event) => {
    if (event && event.preventDefault) event.preventDefault();
    if (event && event.stopPropagation) event.stopPropagation();

    await ui.reset(KEYS.MODAL);
    await ui.setInstance(KEYS.MODAL, {
      title: `${t("TITLES.BALANCE_DETAILS", "Your balance breakdown", {
        ns: "titles",
      })}`,
      name: "balance-details-modal",
      body: <AcBalanceBreakDownModal />,
      closeable: true,
      visible: true,
      callback: async () => {
        await ui.reset(KEYS.MODAL, KEYS.VISIBLE, false);
      },
    });
  };

  const displayAddBalanceModal = async (event) => {
    if (event && event.preventDefault) event.preventDefault();
    if (event && event.stopPropagation) event.stopPropagation();

    await ui.reset(KEYS.MODAL);
    await ui.setInstance(KEYS.MODAL, {
      title: `${t("TITLES.ADD_BALANCE", "Add balance", { ns: "titles" })}`,
      name: "add-balance-modal",
      body: <AcAddBalanceModal />,
      closeable: true,
      visible: true,
      callback: async () => {
        await ui.reset(KEYS.MODAL, KEYS.VISIBLE, false);
      },
    });
  };

  const displayWithdrawBalanceModal = async (event) => {
    if (event && event.preventDefault) event.preventDefault();
    if (event && event.stopPropagation) event.stopPropagation();

    await ui.reset(KEYS.MODAL);
    await ui.setInstance(KEYS.MODAL, {
      title: `${t("TITLES.WITHDRAW_BALANCE", "Withdraw balance", {
        ns: "titles",
      })}`,
      name: "withdraw-balance-modal",
      body: <AcWithdrawBalanceModal />,
      closeable: true,
      visible: true,
      callback: async () => {
        await ui.reset(KEYS.MODAL, KEYS.VISIBLE, false);
      },
    });
  };

  const renderTransactionsList = useMemo(() => {
    if (is_loading) return <AcLoaderInline loading />;
    else if (
      !AcIsSet(current_movements) ||
      !AcIsArray(current_movements) ||
      current_movements.length === 0
    ) {
      return (
        <AcEmptyBlock
          message={t(
            "no-transactions-found",
            "No transactions found to display",
            { ns: "common" }
          )}
        />
      );
    }

    // We only need to display the first 4 items(Should be done per page)
    const items = current_movements && current_movements.slice(0, 4);

    return (
      <Suspense fallback={<AcLoaderInline loading />}>
        <AcTransactionList items={items} />
      </Suspense>
    );
  }, [is_loading, current_movements]);

  const getWithdrawButtonOptions = useMemo(() => {
    return {
      type: TYPES.BUTTON,
      theme: THEMES.OMEGA,
      variant: VARIANTS.OUTLINE,
      title: `${t("TITLES.WITHDRAW_BALANCE", "Withdraw balance", {
        ns: "titles",
      })}`,
      callback: displayWithdrawBalanceModal,
      className: `${current_balance_is_loading ? "d-none" : ""}`,
    };
  }, [current_balance_is_loading]);

  const getAddBalanceButtonOptions = useMemo(() => {
    return {
      type: TYPES.BUTTON,
      theme: THEMES.OMEGA,
      title: `${t("TITLES.ADD_BALANCE", "Add balance", { ns: "titles" })}`,
      callback: displayAddBalanceModal,
    };
  }, []);

  const getBalanceDetailsButtonOptions = useMemo(() => {
    return {
      type: TYPES.BUTTON,
      theme: THEMES.OMEGA,
      variant: VARIANTS.TEXT,
      title: `${t("click-to-view-balance", "Click to view balance details", {
        ns: "dashboard",
      })}`,
      callback: displayBalanceBreakdownModal,
      className: `${
        current_balance_is_loading
          ? "d-none"
          : "h-padding-y-0 h-padding-right-0 h-button-text-right"
      }`,
    };
  }, [displayBalanceBreakdownModal]);

  const handleRangeChange = (key, range) => {
    setSelectedRange(key);
    global.get_chart_data(t, range);
  };

  const renderIncomingOutgoingChart = useMemo(() => {
    if (global.is_loading) return <AcLoaderInline loading />;

    return (
      <Suspense fallback={<AcLoaderInline loading />}>
        <AcIncomingOutgoingChart
          currency={current_partner_currency}
          data={current_chart_data}
          key={"ac-dashboard-overview-incoming-outgoing-chart"}
          range={selectedRange}
          onRangeChange={handleRangeChange}
        />
      </Suspense>
    );
  }, [
    global.is_loading,
    current_chart_data,
    handleRangeChange,
    selectedRange,
    current_partner_currency,
  ]);

  const getGridContainerClassNames = useCallback((uneven = false) => {
    return clsx(_CLASSES.MAIN, uneven && _CLASSES.UNEVEN);
  });

  return (
    <>
      <div className={getGridContainerClassNames(true)}>
        <AcCard
          className={
            "ac-dashboard-overview__column ac-dashboard-overview__column--narrow h-flex-order--xs-2 h-flex-order--sm-2"
          }
          style={{ minHeight: "40rem" }}
          overflow={false}
        >
          <AcImage
            wrpClassName={"ac-balance-details__hero"}
            source={BalanceHero2x}
            key={"ac-balance-hero-image"}
            transparent
          />
          <AcContainer style={{ minHeight: "128px" }}>
            <AcRow>
              <AcColumn xs={6} md={6}>
                <AcHeading
                  theme={THEMES.MEDIUM}
                  rank={6}
                  className={"h-margin-bottom-5"}
                >
                  {t("your-balance", "Your balance", { ns: "dashboard" })}
                </AcHeading>
              </AcColumn>

              <AcColumn xs={6} md={6} className={"h-text--align-right"}>
                <AcHeading
                  theme={THEMES.MEDIUM}
                  rank={6}
                  className={"h-margin-bottom-5"}
                >
                  <AcButton {...getBalanceDetailsButtonOptions}>
                    <span className={"h-hover-omega-dark"}>
                      {t("details", "Details", { ns: "dashboard" })}
                    </span>
                  </AcButton>
                </AcHeading>
              </AcColumn>
            </AcRow>

            <AcRow className={"h-margin-bottom-10"}>
              <AcColumn xs={12} md={12}>
                <AcHeading rank={2} theme={THEMES.PITCH}>
                  {getCurrentBalanceFormatted}
                </AcHeading>
              </AcColumn>
            </AcRow>

            <AcRow className={"h-margin-y-0"}>
              <AcColumn xs={6} md={6} className={"h-text--align-left"}>
                {has_continuous_settlement_interval &&
                  !current_balance_is_loading &&
                    configurations["add-balance"] && (
                    <AcButton {...getAddBalanceButtonOptions}>
                      <AcIcon
                        icon={ICONS.PLUS_CIRCLE_OUTLINE}
                        className={"h-hidden-xxs h-hidden-xs"}
                      />
                      <span className={"h-text-overflow h-padding-y-1"}>
                        {t("add-balance", "Add balance", { ns: "dashboard" })}
                      </span>
                    </AcButton>
                  )}
              </AcColumn>

              <AcColumn xs={6} md={6} className={"h-text--align-right"}>
                {has_continuous_settlement_interval &&
                  !current_balance_is_loading &&
                    configurations.withdrawals &&(
                    <AcButton {...getWithdrawButtonOptions}>
                      <AcIcon
                        icon={ICONS.BANK_OUTLINE}
                        className={"h-hidden-xxs h-hidden-xs"}
                      />
                      <span className={"h-text-overflow h-padding-y-1"}>
                        {t("withdraw-balance", "Withdraw", { ns: "dashboard" })}
                      </span>
                    </AcButton>
                  )}
              </AcColumn>
            </AcRow>
          </AcContainer>
        </AcCard>

        <AcCard
          className={
            "ac-dashboard-overview__column ac-dashboard-overview__column--wide h-padding-x-0 h-padding-y-0 h-flex-order--xs-1 h-flex-order--sm-1"
          }
          overflow={false}
        >
          {current_profile_is_loading || isFetching ? (
            <AcLoaderInline loading />
          ) : (
            <>
              {!twoFactorIsReady ? (
                <AcContainer fluid className={"h-height-100-p"}>
                  <AcRow className={"h-height-100-p"}>
                    <AcColumn
                      xxs={12}
                      xs={12}
                      sm={12}
                      md={12}
                      lg={4}
                      className={
                        "h-flex-order--xxs-2 h-flex-order--xs-2 h-flex-order--sm-2 h-flex-order--md-2"
                      }
                    >
                      <AcCard
                        theme={THEMES.WHITE}
                        className={"h-padding-25 h-height-100-p"}
                      >
                        <AcHeading theme={THEMES.DARK} rank={3} tag={"h2"}>
                          {t("support", "Support", { ns: "dashboard" })}
                        </AcHeading>

                        <AcRichContent
                          content={`<p>${t(
                            "question-support",
                            "Do you have any questions or do you need help? On the support page you can see your tickets and create new ones.",
                            { ns: "dashboard" }
                          )}</p>`}
                        />

                        <AcButton
                          className={"button-info-card"}
                          theme={THEMES.OMEGA}
                          type={TYPES.BUTTON}
                          title={t("go-to-support", "Go to support page", {
                            ns: "dashboard",
                          })}
                          tag={"a"}
                          href={"support"}
                        >
                          <span>
                            {t("go-to-support", "Go to support page", {
                              ns: "dashboard",
                            })}
                          </span>
                          <AcIcon icon={ICONS.OPEN_IN_NEW} />
                        </AcButton>
                      </AcCard>
                    </AcColumn>
                    <AcColumn
                      xxs={12}
                      xs={12}
                      sm={12}
                      md={12}
                      lg={8}
                      className={
                        "h-flex-order--xxs-1 h-flex-order--xs-1 h-flex-order--sm-1 h-flex-order--md-1"
                      }
                    >
                      <AcCard
                        className={"ac-card-overview__help h-padding-y-0"}
                        theme={THEMES.TRANSPARENT}
                        square
                      >
                        <AcImage
                          wrpClassName={"ac-support-details__hero"}
                          source={SupportHero2x}
                          key={"ac-support-hero-image"}
                          transparent
                        />
                      </AcCard>
                    </AcColumn>
                  </AcRow>
                </AcContainer>
              ) : (
                <AcContainer fluid className={"h-height-100-p"}>
                  <AcRow className={"h-height-100-p"}>
                    <AcColumn
                      xxs={12}
                      xs={12}
                      sm={12}
                      md={12}
                      lg={4}
                      className={
                        "h-flex-order--xxs-2 h-flex-order--xs-2 h-flex-order--sm-2 h-flex-order--md-2"
                      }
                    >
                      <AcCard
                        theme={THEMES.WHITE}
                        className={"h-padding-25 h-height-100-p"}
                      >
                        <AcHeading theme={THEMES.DARK} rank={3} tag={"h2"}>
                          {t("2fa", "Two-factor authentication", {
                            ns: "dashboard",
                          })}
                        </AcHeading>

                        <AcRichContent
                          content={`<p>${t(
                            "2fa-info",
                            "Make your account extra secure by setting up two-factor authentication.",
                            { ns: "dashboard" }
                          )}</p>`}
                        />

                        <AcButton
                          className={"button-info-card"}
                          theme={THEMES.OMEGA}
                          type={TYPES.BUTTON}
                          title={t(
                            "set-up-2fa",
                            "Set up two-factor authentication",
                            { ns: "dashboard" }
                          )}
                          tag={"a"}
                          target={"_blank"}
                          href={`${config.pkce.accounts_uri}/dashboard/account-and-security/two-factor/setup/select-method`}
                        >
                          <span>
                            {t(
                              "set-up-2fa",
                              "Set up two-factor authentication",
                              { ns: "dashboard" }
                            )}
                          </span>
                          <AcIcon icon={ICONS.OPEN_IN_NEW} />
                        </AcButton>
                      </AcCard>
                    </AcColumn>
                    <AcColumn
                      xxs={12}
                      xs={12}
                      sm={12}
                      md={12}
                      lg={8}
                      className={
                        "h-flex-order--xxs-1 h-flex-order--xs-1 h-flex-order--sm-1 h-flex-order--md-1"
                      }
                    >
                      <AcCard
                        className={"ac-card-overview__help h-padding-y-0"}
                        theme={THEMES.TRANSPARENT}
                        square
                      >
                        <AcImage
                          wrpClassName={"ac-2fa-details__hero h-height-100-p"}
                          source={TwoFaHero2x}
                          key={"ac-2fa-hero-image"}
                          transparent
                        />
                      </AcCard>
                    </AcColumn>
                  </AcRow>
                </AcContainer>
              )}
            </>
          )}
        </AcCard>
      </div>

      <div className={getGridContainerClassNames(false)}>
        <AcCard
          theme={THEMES.TRANSPARENT}
          dense
          className={
            "ac-dashboard-overview__column ac-dashboard-overview__column--half h-flex-order--xs-1 h-flex-order--sm-1"
          }
        >
          <AcContainer className={"h-padding-x-0"}>
            <AcRow>
              <AcColumn xs={8} sm={6}>
                <AcHeading theme={THEMES.MEDIUM} rank={6}>
                  {t("recent-transactions", "Recent transactions", {
                    ns: "dashboard",
                  })}
                </AcHeading>
              </AcColumn>

              <AcColumn xs={4} sm={6} className={"h-text--align-right"}>
                <Link
                  to={ROUTES.TRANSACTIONS_OVERVIEW.path}
                  className={
                    "ac-link h-margin-left-auto h-margin-right-0 h-v-align-top"
                  }
                >
                  {t("view-all", "View all", { ns: "common" })}
                </Link>
              </AcColumn>
            </AcRow>
            {renderTransactionsList}
          </AcContainer>
        </AcCard>

        <AcCard
          theme={THEMES.TRANSPARENT}
          dense
          className={
            "ac-dashboard-overview__column ac-dashboard-overview__column--half h-flex-order--xs-2 h-flex-order--sm-2"
          }
        >
          <AcContainer>
            <AcRow>
              <AcColumn xs={8} sm={6} className={"h-padding-left-0"}>
                <AcHeading theme={THEMES.MEDIUM} rank={6}>
                  {t("statistics", "Statistics", { ns: "dashboard" })}
                </AcHeading>
              </AcColumn>

              <AcColumn
                xs={4}
                sm={6}
                className={"h-text--align-right h-padding-right-0"}
              >
                <Link
                  to={`${ROUTES.DASHBOARD.path}#stats`}
                  className={"ac-link h-margin-left-auto h-margin-right-0"}
                >
                  {t("view-all", "View all", { ns: "common" })}
                </Link>
              </AcColumn>
            </AcRow>
          </AcContainer>

          <AcCard>{renderIncomingOutgoingChart}</AcCard>
        </AcCard>
      </div>
    </>
  );
};

export default withTranslation(["titles", "dashboard", "common", "store"])(
  withStore(observer(AcDashboardOverviewTab))
);
